import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@v2/shared/lib/utils';
import useReviewStars from '@v2/shared/components/ReviewStars/useReviewStars';
import { CategoryFacet, CategoryFacetValue } from './types';

type Props = {
  facet: CategoryFacet;
  onNavigate: (value: CategoryFacetValue, parameter: string) => void;
};

/** The one facet whose values are drawn rather than written out. */
const RATING_PARAMETER = 'min_rating';

/**
 * How tall a group may get before it scrolls, keyed by facet — the legacy sidebar's own
 * caps (`filters.scss`), kept because they are what stops Country and Language, which
 * run to twenty-odd rows each, from pushing the product grid off the first screen.
 *
 * Categories gets twice the room because it is the one group a visitor came to use, and
 * the two short ones are left uncapped so a scrollbar never appears over four rows.
 */
const HEIGHTS: Record<string, string> = {
  category_id: 'max-h-[400px] overflow-y-auto',
  price_range: '',
  min_rating: '',
};

const DEFAULT_HEIGHT = 'max-h-[200px] overflow-y-auto';

/**
 * Complete strings rather than a base plus an override, because `cn` is clsx: both
 * classes would reach the element and the stylesheet's own order would settle it.
 */
const RESTING_CLASS = 'border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900';

const SELECTED_CLASS = 'border-primary bg-primary-50 font-semibold text-primary';

const ROW_CLASS = cn(
  'flex items-center justify-between gap-2 no-underline',
  'border-0 border-l-[3px] border-solid transition-colors'
);

/**
 * One facet — Categories, Format, Country — as a labelled group of links.
 *
 * Links, and the facet's own href, because that is what was on the page before: see
 * `filterUrl.ts` for why the href and the destination are allowed to differ.
 */
export default function FilterGroup({ facet, onNavigate }: Props) {
  const labelId = `filter-${facet.parameter}`;

  return (
    <div className="border-0 border-t border-solid border-slate-100 py-3 first:border-t-0">
      {/*
        * A div and `aria-labelledby`, not an `h2`. The legacy sidebar's group label was a
        * div, and turning seven of them into headings would put "Country", "Language" and
        * "Price Range" into the document outline of a page whose outline is the one thing
        * this conversion is not allowed to change. The list still names itself to a
        * screen reader, which is what the heading would have bought.
        */}
      <div
        id={labelId}
        className="px-5 pb-1 text-xs font-bold uppercase tracking-wider text-slate-500"
      >
        {facet.title}
      </div>

      <ul
        aria-labelledby={labelId}
        className={cn(
          'm-0 list-none p-0',
          facet.parameter in HEIGHTS ? HEIGHTS[facet.parameter] : DEFAULT_HEIGHT
        )}
      >
        {facet.values.map((value) => (
          <FilterValue
            key={`${facet.parameter}-${value.value}`}
            value={value}
            parameter={facet.parameter}
            depth={0}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </div>
  );
}

function FilterValue({
  value,
  parameter,
  depth,
  onNavigate,
}: {
  value: CategoryFacetValue;
  parameter: string;
  depth: number;
  onNavigate: (value: CategoryFacetValue, parameter: string) => void;
}) {
  return (
    <li>
      <a
        href={value.href}
        aria-current={value.selected ? 'true' : undefined}
        onClick={(event) => {
          // A modified click is the visitor asking for a tab of their own, and the href
          // is a real url, so let the browser have it.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
          }

          event.preventDefault();
          onNavigate(value, parameter);
        }}
        className={cn(
          ROW_CLASS,
          'py-2 pr-4 text-sm',
          value.selected ? SELECTED_CLASS : RESTING_CLASS,
          depth === 0 ? 'pl-5' : 'pl-9'
        )}
      >
        <ValueLabel parameter={parameter} value={value} />
      </a>

      {value.children.length > 0 && (
        <ul className="m-0 list-none p-0">
          {value.children.map((child) => (
            <FilterValue
              key={child.value}
              value={child}
              parameter={parameter}
              depth={depth + 1}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * A rating facet's value is a number of stars, and the legacy sidebar drew it as stars
 * plus "& Up". Drawn here too, with the shared component, so the one facet that is a
 * picture stays a picture.
 */
function ValueLabel({ parameter, value }: { parameter: string; value: CategoryFacetValue }) {
  const rating = Number(value.value);
  const isRating = parameter === RATING_PARAMETER && value.value !== '' && Number.isFinite(rating);

  // The hook, not `ReviewStars` itself: that component always draws a review count
  // beside the stars, and a facet has none to draw.
  const stars = useReviewStars(isRating ? rating : 0);

  if (!isRating) {
    return <span>{value.title}</span>;
  }

  return (
    <span className="flex items-center gap-1 text-xs">
      <span>
        {stars.map((star, index) => (
          <FontAwesomeIcon key={index} icon={star.icon} className="text-amber-500" />
        ))}
      </span>
      {rating < 5 && <span>&amp; Up</span>}
    </span>
  );
}
